import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import style from './style.module.css';
import classNames from 'classnames';
import BidirectionalStepper from "design-v2/common/BidirectionalStepper/index";
import moment, { Moment } from 'moment';
import { LocaleService } from 'internal-services/locale/locale.service';
import { IDatePicker } from './types';
import {StepperSize} from "design-v2/common/BidirectionalStepper/types";
import { Button, Grid, Paper, Typography, useMediaQuery } from '@material-ui/core';
import DatePickerOverlay from 'external-components/date-picker-overlay/index';
import typ from "design-v2/globals/typography.module.css";
import colors from "design-v2/globals/colors/colors.module.css";
import ModalContainer from 'design-v2/common/ModalContainer';
import Link from "@material-ui/core/Link";
import PreventDoubleClickToZoom from "design-v2/common/PreventDoubleClickToZoom/index";
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { APP_COLORS, Colors } from 'design-v2/globals/colors/colors';

const defaultState = {
  left: false,
  right: false,
  mid: false,
};

export const btnIdPrefix = 'btn-test-id-';

const DatePicker = (props: IDatePicker) => {
  const {
    minWidth = 160,//136,
    stepperSize = StepperSize.regular,
    currentDate,
    disablePastDate = false,
    disableFutureDate = false,
    handleDateUpdate,
    maxDate,
    dateFormat = null,
    datePreset = null, // LocaleService preset for locale-aware button label
    whiteBackground,
    pickerContainerClass,
    isModalView,
    isSimpleMode,
    textAlign,
    isWideVersion = false,
    isFloorPlan = false,
    closeOnOuterClick = false,
    showTodayAsDot = false,
    renderDay
  } = props;

  const wrapperRef: any = useRef(null);
  const datePickerRef: any = useRef(null);

  const [showPicker, setShowPicker] = useState(false);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [disableState, setDisableState] = useState(defaultState);
  const [isToday, setIsToday] = useState(false);

  // this hooks check for outside clicks and closes the datepicker
  function useOutsideClickRecognition(ref: any) {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowPicker(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }

  if (closeOnOuterClick ) {
    // if not handheld decices
    if (!isModalView) {
      // to close the DatePicker if clicked outside the datepicker
      useOutsideClickRecognition(datePickerRef);
    }
  }


  useEffect(() => {
    if(currentDate) setSelectedDate(formatDate(currentDate))
    else setSelectedDate(null);
  }, [currentDate])

  useEffect(() => {
    if(disablePastDate) {
      if(moment().isAfter(moment(selectedDate).add(-1, 'days'))) {
        setDisableState({
          ...disableState,
          left: true,
        })
      }else {
        setDisableState({
          ...disableState,
          left: false,
        })
      }
    }

    if(disableFutureDate) {
      if(moment().isBefore(moment(selectedDate).add(1, 'days'))) {
        setDisableState({
          ...disableState,
          right: true,
        })
      }else {
        setDisableState({
          ...disableState,
          right: false,
        })
      }
    }
  }, [selectedDate]);

  const {selectedDateMom, selectedDateAsText} = useMemo(() => {
    LocaleService.configureMomentLocale();
    // Explicit format: selectedDate is YYYY-MM-DD from formatDate — avoids ambiguous string parsing.
    const selectedDateMom = moment(selectedDate, 'YYYY-MM-DD'); 
    // for floorplan datepicker we need to show the selected date text as Today Thu, 2 May
    if (isFloorPlan) {
      const isToday = selectedDateMom.isSame(moment(), 'date');
      setIsToday(isToday);
    }

    // Prefer datePreset (locale-aware) → fall back to legacy dateFormat (moment pattern) → default preset
    const selectedDateAsText = datePreset
      ? LocaleService.formatDate(selectedDateMom.toDate(), datePreset as any)
      : dateFormat
        ? selectedDateMom.format(dateFormat)
        : LocaleService.formatDate(selectedDateMom.toDate(), 'weekday-medium');

    return { selectedDateMom, selectedDateAsText };
  }, [selectedDate]);

  const formatDate = useCallback((date: Moment | Date | string): string => {
    LocaleService.configureMomentLocale();
    return moment(date).format('YYYY-MM-DD');
  }, []);

  const handleDateChange = (selectedDate: Moment) => {
    setShowPicker(false);
    setSelectedDate(formatDate(selectedDate));
    handleDateUpdate && handleDateUpdate(selectedDate);
  };

  const handleLeftClick = () => {
    if (selectedDateMom) {
      const newDate = selectedDateMom.add(-1, 'days');
      setSelectedDate(formatDate(newDate));
      handleDateChange(newDate)
    }

    showPicker && setShowPicker(false);
  };

  const handleRightClick = () => {
    if (selectedDateMom) {
      const newDate = selectedDateMom.add(1, 'days');
      setSelectedDate(formatDate(newDate));
      handleDateChange(newDate)
    }

    showPicker && setShowPicker(false);
  };

  const handleMiddleClick = () => {
    // for floorplan view date picker it needs to be closed if pressed on the middle part again
    if (isFloorPlan) {
      setShowPicker(!showPicker);
    } else {
      !showPicker && setShowPicker(true);
    }
  };

  const handleUnFocus = () => {
    !showPicker && setShowPicker(false);
  }

  const TodayButton = () => {
    return (
      <PreventDoubleClickToZoom>
        <div className={style.todayBtnContainer}>
          <Button
            data-testid={btnIdPrefix + 'today'}
            disableRipple
            className={classNames({
              [style.btnText]: true,
              [typ.typSize4]: true,
              [typ.typReg]: true,
            })}
            variant="outlined"
            disabled={isToday}
            color="primary"
            onClick={() => {
              handleDateChange(moment());
            }}
          >
            Today
          </Button>
        </div>
      </PreventDoubleClickToZoom>
    );
  };

  return (
    <div className={style.root}>
      <div
        className={classNames({
          [style.datePickerContainer]: true,
          [style.datePickerWhiteContainer]: whiteBackground,
        })}
        ref={datePickerRef}
      >
        <div>
          {isSimpleMode && (
            <div className={style.simpleMode}>
              <Typography variant="body1" className={style.simpleModeText} data-testid="selectedDateAsText">
                {selectedDateAsText}
              </Typography>

              <Link
                // disabled={props.disableChange}
                component="button"
                onClick={handleMiddleClick}
                className={style.changeButton}
                data-testid={"chooseDateBtn"}
              >
                Choose date
              </Link>
            </div>
          )}
          {!isSimpleMode && (
            <PreventDoubleClickToZoom>
              <BidirectionalStepper
                disableState={disableState}
                minWidth={minWidth}
                stepperSize={stepperSize}
                handleLeftClick={handleLeftClick}
                handleRightClick={handleRightClick}
                handleMiddleClick={handleMiddleClick}
                handleUnFocus={handleUnFocus}
                enableUnFocusForMouseLeave={false}
                parentRef={wrapperRef}
                isFloorPlan={isFloorPlan}
              >
                <div
                  className={classNames({
                    [style.childContainer]: true
                  })}
                  style={textAlign ? {
                    justifyContent: textAlign,
                  } : null}
                >
                  <span
                    className={classNames({
                      [typ.appWrap]: true,
                      [typ.typSize2]: stepperSize === StepperSize.regular,
                      [typ.typSize3]: stepperSize === StepperSize.small,
                      [typ.typMed]: true,
                      [colors.clrNbiMagenta]: !isFloorPlan
                    })}
                  >
                    {isToday && selectedDate &&
                      <span className={classNames({
                      [style.selectedTodayTextFp]: true,
                      [style.selectedDateTextFp]: true,
                    })}>
                      {showTodayAsDot ? <span style={{ padding: '0 5px' }}><FiberManualRecordIcon
                        style={{ color: Colors.get(APP_COLORS.clrGood), fontSize: '1.2rem' }} /></span> : `Today `}
                    </span>}

                    <span className={classNames({
                      [style.selectedDateTextFp]: true,
                    })}>{selectedDate ? selectedDateAsText : null}</span>
                  </span>
                </div>
              </BidirectionalStepper>
            </PreventDoubleClickToZoom>
          )}
        </div>
        {
          !isModalView && showPicker &&
          <div
            data-testid={'date-picker-over-lay-container-id'}
            ref={wrapperRef}
            className={classNames(pickerContainerClass, {
              [style.pickerContainer]: true,
            })}
          >
            <Paper elevation={3}>
              <PreventDoubleClickToZoom>
                <DatePickerOverlay
                  maxDate={maxDate}
                  currentDate={selectedDateMom}
                  handleDateUpdate={(date: moment.Moment) => handleDateChange(date)}
                  disableFutureDate={disableFutureDate}
                  disablePastDate={disablePastDate}
                  staticType={true}
                  todayBtn={true}
                  className={true}
                  renderDay={renderDay}
                >
                  <TodayButton />
                </DatePickerOverlay>
              </PreventDoubleClickToZoom>
            </Paper>
          </div>
        }
        {isModalView && (
          <ModalContainer
            isOpen={showPicker}
            onRequestClose={() => setShowPicker(false)}
            isWideVersion={isWideVersion}
            wideRight={130}
          >
              <PreventDoubleClickToZoom>
                <DatePickerOverlay
                  maxDate={maxDate}
                  currentDate={selectedDateMom}
                  handleDateUpdate={(date: moment.Moment) => handleDateChange(date)}
                  disableFutureDate={disableFutureDate}
                  disablePastDate={disablePastDate}
                  staticType={true}
                  todayBtn={true}
                  className={true}
                  renderDay={renderDay}
                >
                  <TodayButton />
                </DatePickerOverlay>
              </PreventDoubleClickToZoom>
          </ModalContainer>
        )}
      </div>
    </div>
  )
}

export default DatePicker;
